/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  User,
  Mail,
  Award,
  Trophy,
  Shield,
  Trash2,
  LogOut,
  Check,
  Edit3,
  Calendar,
  TrendingUp,
  Compass,
  Database,
  CloudLightning,
  RefreshCw,
  Sparkles,
  Info,
  Clock,
  ArrowLeft,
  Users,
  X,
  Bell,
  Github
} from 'lucide-react';
import { updateProfile } from 'firebase/auth';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot
} from 'firebase/firestore';
import { db, isFirestoreQuotaError, notifyQuotaExceeded } from '../firebase';
import { ALL_STICKERS, STICKER_MAP, TEAMS, POS_ICON, POS_CLR, ACHIEVEMENTS } from '../constants';
import { Sticker, Rarity } from '../types';
import { Avatar } from './Avatar';
import { WC_AVATARS } from '../avatars';
import { StickerCard } from './StickerItem';
import { LanguageCode, TRANSLATIONS } from '../translations';

interface UserProfileProps {
  user: any;
  owned: Set<string>;
  duplicates: string[];
  packs: number;
  connectOpen: boolean;
  onToggleConnect: (val: boolean) => Promise<void>;
  onLogOut: () => void;
  onResetProgress: () => Promise<void>;
  onBack: () => void;
  onUpdateUser: (newUser: any) => void;
  onAddPacks?: (amount: number) => Promise<void>;
  onUpdateInventory?: (owned: Set<string>, duplicates: string[], packs?: number) => Promise<void>;
  notificationPermission?: string;
  onRequestNotificationPermission?: () => Promise<string>;
  streak?: number;
  achievements?: string[];
  lang?: LanguageCode;
}

interface FriendProfile {
  userId: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  owned?: string[];
  duplicates?: string[];
}

interface Friendship {
  id: string;
  users: string[];
  status: 'pending' | 'accepted';
}

interface Gift {
  id: string;
  senderId: string;
  senderEmail: string;
  senderName: string;
  receiverId: string;
  receiverEmail: string;
  stickerId: string;
  status: 'pending' | 'claimed';
  createdAt: string;
}

export const UserProfile = ({
                              user,
                              owned,
                              duplicates,
                              connectOpen,
                              onToggleConnect,
                              onLogOut,
                              onResetProgress,
                              onBack,
                              onUpdateUser,
                              packs,
                              onAddPacks,
                              onUpdateInventory,
                              notificationPermission = 'default',
                              onRequestNotificationPermission,
                              streak = 1,
                              achievements = [],
                              lang = 'en'
                            }: UserProfileProps) => {
  const t = (key: string) => {
    const activeLang = lang || 'en';
    return TRANSLATIONS[activeLang]?.[key] || TRANSLATIONS['en']?.[key] || key;
  };

  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [isEditing, setIsEditing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // GitHub Sync specific state variables
  const [githubToken, setGithubToken] = useState<string | null>(typeof window !== 'undefined' ? localStorage.getItem('github_token') : null);
  const [githubUser, setGithubUser] = useState<{ login: string; avatar_url: string } | null>(null);
  const [isSyncingGithub, setIsSyncingGithub] = useState(false);
  const [githubStatus, setGithubStatus] = useState<'idle' | 'checking' | 'syncing' | 'error' | 'success_backup' | 'success_restore'>('idle');
  const [githubMessage, setGithubMessage] = useState('');
  const [manualTokenInput, setManualTokenInput] = useState('');
  const [showManualToken, setShowManualToken] = useState(false);

  const fetchGithubUser = async (token: string) => {
    try {
      setGithubStatus('checking');
      const res = await fetch('https://api.github.com/user', {
        headers: {
          'Authorization': `token ${token}`,
          'Accept': 'application/vnd.github.v3+json'
        }
      });
      if (res.ok) {
        const data = await res.json();
        setGithubUser({
          login: data.login,
          avatar_url: data.avatar_url
        });
        setGithubStatus('idle');
      } else {
        localStorage.removeItem('github_token');
        setGithubToken(null);
        setGithubStatus('error');
        setGithubMessage(lang === 'es' ? 'Tu sesión enlazada de GitHub ha expirado. Por favor, reconecta.' : 'Your linked GitHub session has expired. Please reconnect.');
      }
    } catch (e) {
      console.error(e);
      setGithubStatus('error');
      setGithubMessage(lang === 'es' ? 'No se pudo establecer un canal seguro con la API de GitHub.' : 'Could not establish secure handshake with GitHub API.');
    }
  };

  React.useEffect(() => {
    if (githubToken) {
      fetchGithubUser(githubToken);
    }
  }, [githubToken]);

  React.useEffect(() => {
    const handleGithubMessage = (event: MessageEvent) => {
      const origin = event.origin;
      if (!origin.endsWith('.run.app') && !origin.includes('localhost')) {
        return;
      }
      if (event.data?.type === 'GITHUB_OAUTH_SUCCESS' && event.data?.token) {
        const token = event.data.token;
        localStorage.setItem('github_token', token);
        setGithubToken(token);
        fetchGithubUser(token);
      }
    };
    window.addEventListener('message', handleGithubMessage);
    return () => window.removeEventListener('message', handleGithubMessage);
  }, [lang]);

  const handleConnectGithub = async () => {
    try {
      setGithubStatus('checking');
      setGithubMessage('');

      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const response = await fetch(`/api/auth/github/url?origin=${encodeURIComponent(origin)}`);
      const resJson = await response.json();

      if (resJson.error || !resJson.clientIdConfigured) {
        setGithubStatus('error');
        setGithubMessage(resJson.error || 'OAuth client credentials not configured.');
        setShowManualToken(true);
        return;
      }

      const authWindow = window.open(
          resJson.url,
          'github_oauth_popup',
          'width=600,height=700'
      );

      if (!authWindow) {
        setGithubStatus('error');
        setGithubMessage(lang === 'es' ? 'Popup de autenticación bloqueado. Por favor, habilita popups para sincronizar.' : 'Authentication popup blocked. Please enable popups before syncing.');
      }
    } catch (e) {
      console.error(e);
      setGithubStatus('error');
      setGithubMessage(lang === 'es' ? 'No se pudo iniciar la secuencia de conexión con GitHub.' : 'Could not initiate GitHub connection sequence.');
    }
  };

  const handleGithubBackup = async () => {
    if (!githubToken) return;
    setIsSyncingGithub(true);
    setGithubStatus('syncing');
    setGithubMessage(lang === 'es' ? 'Localizando registros anteriores...' : 'Locating previous ledger records...');

    try {
      const albumData = {
        owned: Array.from(owned),
        duplicates: duplicates,
        packs: packs,
        streak: streak,
        achievements: achievements,
        email: user?.email,
        updatedAt: new Date().toISOString()
      };

      const gistsRes = await fetch('https://api.github.com/gists', {
        headers: {
          'Authorization': `token ${githubToken}`,
          'Accept': 'application/vnd.github.v3+json'
        }
      });

      let existingGistId = null;
      if (gistsRes.ok) {
        const gists = await gistsRes.json();
        const paganiniGist = gists.find((g: any) => g.description === 'Paganini World Cup Album Sync');
        if (paganiniGist) {
          existingGistId = paganiniGist.id;
        }
      }

      const fileContent = JSON.stringify(albumData, null, 2);
      let saveRes;

      if (existingGistId) {
        setGithubMessage(lang === 'es' ? 'Sincronizando particiones de datos activas...' : 'Syncing active database partitions...');
        saveRes = await fetch(`https://api.github.com/gists/${existingGistId}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `token ${githubToken}`,
            'Content-Type': 'application/json',
            'Accept': 'application/vnd.github.v3+json'
          },
          body: JSON.stringify({
            files: {
              'paganini-album-sync.json': {
                content: fileContent
              }
            }
          })
        });
      } else {
        setGithubMessage(lang === 'es' ? 'Creando respaldo de seguridad en la nube...' : 'Creating cloud backup record...');
        saveRes = await fetch('https://api.github.com/gists', {
          method: 'POST',
          headers: {
            'Authorization': `token ${githubToken}`,
            'Content-Type': 'application/json',
            'Accept': 'application/vnd.github.v3+json'
          },
          body: JSON.stringify({
            description: 'Paganini World Cup Album Sync',
            public: false,
            files: {
              'paganini-album-sync.json': {
                content: fileContent
              }
            }
          })
        });
      }

      if (saveRes.ok) {
        setGithubStatus('success_backup');
        setGithubMessage(lang === 'es' ? '¡Tu álbum de estampas ha sido guardado exitosamente en GitHub!' : 'Sticker album successfully saved to your GitHub secure backend!');
      } else {
        setGithubStatus('error');
        setGithubMessage(lang === 'es' ? 'Error al guardar el respaldo en tus GitHub Gists.' : 'Failed to save cloud sync file to your GitHub Gists.');
      }
    } catch (e) {
      console.error(e);
      setGithubStatus('error');
      setGithubMessage(lang === 'es' ? 'Excepción ocurrida al escribir en las APIs de GitHub.' : 'Exception encountered trying to write to GitHub APIs.');
    } finally {
      setIsSyncingGithub(false);
    }
  };

  const handleGithubRestore = async () => {
    if (!githubToken) return;
    setIsSyncingGithub(true);
    setGithubStatus('syncing');
    setGithubMessage(lang === 'es' ? 'Estableciendo conexión con los nodos de GitHub...' : 'Connecting to GitHub record nodes...');

    try {
      const gistsRes = await fetch('https://api.github.com/gists', {
        headers: {
          'Authorization': `token ${githubToken}`,
          'Accept': 'application/vnd.github.v3+json'
        }
      });

      if (!gistsRes.ok) {
        setGithubStatus('error');
        setGithubMessage(lang === 'es' ? 'No se pudieron recuperar los gists de tu cuenta.' : 'Could not retrieve gists from your account descriptor.');
        return;
      }

      const gists = await gistsRes.json();
      const paganiniGist = gists.find((g: any) => g.description === 'Paganini World Cup Album Sync');

      if (!paganiniGist) {
        setGithubStatus('error');
        setGithubMessage(lang === 'es' ? 'No se encontró ningún respaldo de álbum en tu GitHub.' : 'No cloud backup found on your GitHub account to restore.');
        return;
      }

      const fileKey = 'paganini-album-sync.json';
      const fileDataObj = paganiniGist.files[fileKey];
      if (!fileDataObj || !fileDataObj.raw_url) {
        setGithubStatus('error');
        setGithubMessage('Backup file content description is empty.');
        return;
      }

      const rawRes = await fetch(fileDataObj.raw_url);
      if (!rawRes.ok) {
        setGithubStatus('error');
        setGithubMessage(lang === 'es' ? 'No se pudieron leer los detalles del respaldo.' : 'Error reading backup payload details.');
        return;
      }

      const backupObj = await rawRes.json();

      if (onUpdateInventory && backupObj.owned) {
        const ownedSet = new Set<string>(backupObj.owned);
        await onUpdateInventory(ownedSet, backupObj.duplicates || [], backupObj.packs || 0);

        if (user?.uid) {
          const userDocRef = doc(db, 'users', user.uid);
          await setDoc(userDocRef, {
            owned: backupObj.owned,
            duplicates: backupObj.duplicates || [],
            packs: backupObj.packs || 0,
            streak: backupObj.streak || streak,
            achievements: backupObj.achievements || achievements,
            updatedAt: new Date().toISOString()
          }, { merge: true });
        }

        setGithubStatus('success_restore');
        setGithubMessage(lang === 'es' ? '¡Álbum restaurado y sincronizado exitosamente!' : 'Album recovery completed successfully! Synced local and Cloud databases.');
      } else {
        setGithubStatus('error');
        setGithubMessage(lang === 'es' ? 'Estructura de respaldo inválida o vacía.' : 'Invalid inventory payload in cloud backup file.');
      }
    } catch (e) {
      console.error(e);
      setGithubStatus('error');
      setGithubMessage(lang === 'es' ? 'La restauración falló debido a problemas de red.' : 'Restore failed during decryption/network handshakes.');
    } finally {
      setIsSyncingGithub(false);
    }
  };

  const handleManualTokenSubmit = () => {
    if (!manualTokenInput.trim()) return;
    const token = manualTokenInput.trim();
    localStorage.setItem('github_token', token);
    setGithubToken(token);
    fetchGithubUser(token);
    setManualTokenInput('');
  };

  const handleDisconnectGithub = () => {
    localStorage.removeItem('github_token');
    setGithubToken(null);
    setGithubUser(null);
    setGithubStatus('idle');
    setGithubMessage('');
  };

  // Community connection beacon toggle states
  const [loadingConnect, setLoadingConnect] = useState(false);

  // Duplicate spares inspect states
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [selectedInspectSticker, setSelectedInspectSticker] = useState<Sticker | null>(null);

  // Map and sort duplicate spares
  const uniqueDuplicateIds = Array.from(new Set(duplicates));
  const duplicateStickers = uniqueDuplicateIds
      .map(id => STICKER_MAP[id])
      .filter((s): s is Sticker => !!s);

  const sortedDuplicateStickers = [...duplicateStickers].sort((a, b) => {
    const rarityOrder = { legendary: 3, rare: 2, common: 1 };
    const diff = rarityOrder[b.rarity] - rarityOrder[a.rarity];
    if (diff !== 0) return diff;
    return a.num - b.num;
  });

  // Gifting and friends status management
  const [friends, setFriends] = useState<FriendProfile[]>([]);
  const [receivedGifts, setReceivedGifts] = useState<Gift[]>([]);
  const [giftingInProgress, setGiftingInProgress] = useState<string | null>(null);
  const [giftSuccessMessage, setGiftSuccessMessage] = useState<string | null>(null);
  const [claimingGiftId, setClaimingGiftId] = useState<string | null>(null);

  const myUid = user?.uid;

  // Real-time listener to fetch accepted friends
  React.useEffect(() => {
    if (!myUid) return;
    const q = query(
        collection(db, 'friendships'),
        where('users', 'array-contains', myUid),
        where('status', '==', 'accepted')
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const uidsToFetch = new Set<string>();
      snapshot.forEach((doc) => {
        const data = doc.data() as Friendship;
        const otherUid = data.users.find(id => id !== myUid);
        if (otherUid) {
          uidsToFetch.add(otherUid);
        }
      });

      const loadedFriends: FriendProfile[] = [];
      for (const uid of uidsToFetch) {
        try {
          const userSnap = await getDocs(query(collection(db, 'users'), where('userId', '==', uid)));
          if (!userSnap.empty) {
            loadedFriends.push(userSnap.docs[0].data() as FriendProfile);
          }
        } catch (err) {
          console.error("Could not fetch profile for friend ID: ", uid, err);
        }
      }
      setFriends(loadedFriends);
    }, (error) => {
      console.error("Friends subscription failed inside profile: ", error);
      if (isFirestoreQuotaError(error)) {
        notifyQuotaExceeded();
      }
    });

    return () => unsubscribe();
  }, [myUid]);

  // Real-time listener for incoming pending gifts
  React.useEffect(() => {
    if (!myUid) return;
    const q = query(
        collection(db, 'gifts'),
        where('receiverId', '==', myUid),
        where('status', '==', 'pending')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list: Gift[] = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() } as Gift);
      });
      setReceivedGifts(list);
    }, (error) => {
      console.error("Gifts notification listener failed inside profile: ", error);
      if (isFirestoreQuotaError(error)) {
        notifyQuotaExceeded();
      }
    });

    return () => unsubscribe();
  }, [myUid]);

  // Handle claiming gift
  const handleClaimGift = async (gift: Gift) => {
    setClaimingGiftId(gift.id);
    try {
      const nextOwned = new Set(owned);
      const nextDuplicates = [...duplicates];

      if (nextOwned.has(gift.stickerId)) {
        nextDuplicates.push(gift.stickerId);
      } else {
        nextOwned.add(gift.stickerId);
      }

      if (onUpdateInventory) {
        await onUpdateInventory(nextOwned, nextDuplicates);
      }

      await deleteDoc(doc(db, 'gifts', gift.id));
    } catch (err) {
      console.error("Failed to claim gift to inventory:", err);
    } finally {
      setClaimingGiftId(null);
    }
  };

  // Handle sending gift
  const handleSendGift = async (friend: FriendProfile) => {
    if (!selectedInspectSticker || !user) return;
    setGiftingInProgress(friend.userId);
    setGiftSuccessMessage(null);
    try {
      const giftId = 'gift_' + Math.random().toString(36).substr(2, 9);
      const giftRef = doc(db, 'gifts', giftId);

      await setDoc(giftRef, {
        id: giftId,
        senderId: user.uid,
        senderEmail: user.email || '',
        senderName: user.displayName || user.email?.split('@')[0] || 'Collector',
        receiverId: friend.userId,
        receiverEmail: friend.email,
        stickerId: selectedInspectSticker.id,
        status: 'pending',
        createdAt: new Date().toISOString()
      });

      const stickerIndex = duplicates.indexOf(selectedInspectSticker.id);
      if (stickerIndex > -1) {
        const nextDuplicates = [...duplicates];
        nextDuplicates.splice(stickerIndex, 1);
        if (onUpdateInventory) {
          await onUpdateInventory(owned, nextDuplicates);
        }
      }

      setGiftSuccessMessage(`Successfully sent ${selectedInspectSticker.name} as a gift to ${friend.displayName || friend.email.split('@')[0]}!`);

      const remainingCount = duplicates.filter(id => id === selectedInspectSticker.id).length - 1;
      if (remainingCount <= 0) {
        setTimeout(() => {
          setSelectedInspectSticker(null);
          setGiftSuccessMessage(null);
        }, 2200);
      }
    } catch (err) {
      console.error("Failed to post gift transaction:", err);
    } finally {
      setGiftingInProgress(null);
    }
  };

  const handleToggleConnect = async () => {
    setLoadingConnect(true);
    setUpdateError(null);
    setUpdateSuccess(false);
    try {
      const nextVal = !connectOpen;
      await onToggleConnect(nextVal);
      setUpdateSuccess(true);
      setTimeout(() => setUpdateSuccess(false), 2000);
    } catch (err: any) {
      console.error(err);
      setUpdateError(err.message || t('failedUpdateDiscovery'));
    } finally {
      setLoadingConnect(false);
    }
  };

  // Custom Avatar Picker details
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);

  const handleSelectAvatar = async (avatarId: string) => {
    setUpdating(true);
    setUpdateError(null);
    setUpdateSuccess(false);

    try {
      if (user) {
        await updateProfile(user, { photoURL: avatarId });
        if (typeof user.reload === 'function') {
          await user.reload();
        }
        onUpdateUser({ ...user });
        setUpdateSuccess(true);
        setShowAvatarPicker(false);
        setTimeout(() => setUpdateSuccess(false), 3000);
      }
    } catch (err: any) {
      console.error(err);
      setUpdateError(err.message || t('errorAvatar'));
    } finally {
      setUpdating(false);
    }
  };

  // Reset confirmation sequence
  const [resetInput, setResetInput] = useState('');
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Compute collector metrics
  const totalStickers = ALL_STICKERS.length;
  const ownedCount = owned.size;
  const uniquePct = totalStickers > 0 ? Math.round((ownedCount / totalStickers) * 100) : 0;

  // Categorized Rarity counts
  const stickersByRarity = {
    common: ALL_STICKERS.filter(s => s.rarity === 'common'),
    rare: ALL_STICKERS.filter(s => s.rarity === 'rare'),
    legendary: ALL_STICKERS.filter(s => s.rarity === 'legendary')
  };

  const ownedByRarity = {
    common: Array.from(owned).filter(id => STICKER_MAP[id]?.rarity === 'common').length,
    rare: Array.from(owned).filter(id => STICKER_MAP[id]?.rarity === 'rare').length,
    legendary: Array.from(owned).filter(id => STICKER_MAP[id]?.rarity === 'legendary').length
  };

  // Rarity completion calculations
  const rarityPcts = {
    common: stickersByRarity.common.length > 0 ? Math.round((ownedByRarity.common / stickersByRarity.common.length) * 100) : 0,
    rare: stickersByRarity.rare.length > 0 ? Math.round((ownedByRarity.rare / stickersByRarity.rare.length) * 100) : 0,
    legendary: stickersByRarity.legendary.length > 0 ? Math.round((ownedByRarity.legendary / stickersByRarity.legendary.length) * 100) : 0
  };

  // Best/Legendary collection highlights
  const legendaryStickersCollected = Array.from(owned)
      .map(id => STICKER_MAP[id])
      .filter(s => s && s.rarity === 'legendary')
      .slice(0, 5);

  // Badge rank
  let rankKey = "rookieFan";
  let rankColor = "text-slate-400 border-slate-700 bg-slate-800/20";
  if (user?.email === 'gonzos978@gmail.com') {
    rankKey = "superuser";
    rankColor = "text-rose-400 border-rose-500/40 bg-rose-500/10 shadow-[0_0_15px_rgba(244,63,94,0.2)]";
  } else if (uniquePct >= 90) {
    rankKey = "fifaLegend";
    rankColor = "text-amber-400 border-amber-500/30 bg-amber-500/10 shadow-[0_0_15px_rgba(245,158,11,0.2)]";
  } else if (uniquePct >= 60) {
    rankKey = "eliteCurator";
    rankColor = "text-blue-400 border-blue-500/30 bg-blue-500/10";
  } else if (uniquePct >= 30) {
    rankKey = "proCollector";
    rankColor = "text-emerald-400 border-emerald-500/30 bg-emerald-500/10";
  } else if (uniquePct >= 10) {
    rankKey = "noviceScout";
    rankColor = "text-purple-400 border-purple-500/30 bg-purple-500/10";
  }
  const rankLabel = t(rankKey);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) {
      setUpdateError(t('displayNameEmpty'));
      return;
    }
    setUpdating(true);
    setUpdateError(null);
    setUpdateSuccess(false);

    try {
      if (user) {
        await updateProfile(user, { displayName: displayName.trim() });
        // Force refresh current user object references to push state upstream
        if (typeof user.reload === 'function') {
          await user.reload();
        }
        onUpdateUser({ ...user });
        setUpdateSuccess(true);
        setIsEditing(false);
        setTimeout(() => setUpdateSuccess(false), 3000);
      }
    } catch (err: any) {
      console.error(err);
      setUpdateError(err.message || t('errorProfile'));
    } finally {
      setUpdating(false);
    }
  };

  const executeReset = async () => {
    if (resetInput !== 'RESET') return;
    setResetting(true);
    try {
      await onResetProgress();
      setShowResetConfirm(false);
      setResetInput('');
    } catch (err) {
      console.error(err);
    } finally {
      setResetting(false);
    }
  };

  // Creation date formatting
  const joinedDate = user?.metadata?.createdAt
      ? new Date(Number(user.metadata.createdAt)).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
      : new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

  return (
      <div className="max-w-4xl mx-auto px-4 py-8 select-none">
        {/* Back to Album Navigation */}
        <button
            onClick={onBack}
            className="inline-flex items-center gap-2 text-xs font-mono text-slate-400 hover:text-white mb-6 uppercase tracking-wider transition-colors cursor-pointer group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          {t('backToAlbum')}
        </button>

        {/* Main Structural Layout Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Left Side: Avatar & Edit Block */}
          <div className="space-y-6">
            <div className="bg-[#1e293b] border border-slate-800 rounded-xl p-6 relative overflow-hidden text-center shadow-xl">
              {/* Background Glow */}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-emerald-500" />
              <div className="absolute scale-150 top-[-50%] left-[-20%] w-[140%] h-[140%] opacity-5 pointer-events-none rounded-full bg-blue-500 blur-[60px]" />

              {/* Avatar Visualizer */}
              <div className="relative inline-block mx-auto mb-2">
                <Avatar
                    photoURL={user?.photoURL}
                    email={user?.email}
                    size="xl"
                    onClick={() => setShowAvatarPicker(!showAvatarPicker)}
                    className="cursor-pointer hover:scale-105 active:scale-95 transition-all duration-200 border-2 border-blue-500 shadow-xl"
                />
                <div className="absolute bottom-0 right-0 p-1.5 bg-blue-600 rounded-full border border-[#1e293b] text-white shadow">
                  <Shield size={10} />
                </div>
              </div>

              <button
                  onClick={() => setShowAvatarPicker(!showAvatarPicker)}
                  className="mb-4 text-[10px] font-mono font-bold uppercase tracking-wider text-blue-400 hover:text-blue-300 transition-colors cursor-pointer flex items-center justify-center gap-1.5 mx-auto py-1 px-2 hover:bg-slate-800/60 rounded"
              >
                <Sparkles size={11} className="text-amber-400 animate-pulse" /> {t('changeAvatar')}
              </button>

              {/* Account Title Details */}
              <div className="space-y-1">
                <div className="flex items-center justify-center gap-1.5">
                  <h2 className="font-bold text-lg text-white font-sans max-w-[180px] truncate">
                    {user?.displayName || 'Collector'}
                  </h2>
                  {!isEditing && (
                      <button
                          onClick={() => setIsEditing(true)}
                          className="p-1 text-slate-500 hover:text-blue-400 rounded-full transition-colors cursor-pointer"
                          title="Edit nickname"
                      >
                        <Edit3 size={12} />
                      </button>
                  )}
                </div>
                <p className="text-[10px] text-slate-500 font-mono truncate">{user?.email}</p>
              </div>

              {/* Rank Flag Indicator */}
              <div className="mt-4 flex justify-center">
              <span className={`px-2.5 py-1 text-[9px] font-mono tracking-wider font-bold rounded-full border uppercase ${rankColor}`}>
                {rankLabel}
              </span>
              </div>

              {/* World Cup Avatar Selection Tray */}
              {showAvatarPicker && (
                  <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mt-5 border-t border-slate-800/60 pt-4 text-left"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[9px] font-mono text-slate-400 font-bold uppercase tracking-wide">{t('selectEmblem')}</span>
                      <button
                          onClick={() => setShowAvatarPicker(false)}
                          className="text-[9px] font-mono text-slate-500 hover:text-white uppercase font-bold"
                      >
                        {t('close')}
                      </button>
                    </div>

                    <div className="grid grid-cols-4 gap-2 bg-slate-950/40 p-2 rounded-lg border border-slate-900">
                      {WC_AVATARS.map((av) => {
                        const isSelected = user?.photoURL === av.id;
                        return (
                            <button
                                key={av.id}
                                type="button"
                                onClick={() => handleSelectAvatar(av.id)}
                                className={`aspect-square rounded-lg flex flex-col items-center justify-center relative transition-all group hover:scale-105 active:scale-95 cursor-pointer p-0.5 ${
                                    av.bgClass
                                } ${
                                    isSelected ? 'ring-2 ring-blue-500 shadow-lg' : 'opacity-80 hover:opacity-100'
                                }`}
                                title={`${av.name} (${av.sub})`}
                            >
                              <span className="text-xl filter drop-shadow select-none leading-none mb-1">{av.icon}</span>
                              <span className={`text-[6px] font-mono block font-black uppercase tracking-tighter ${
                                  av.textColor || 'text-white'
                              } opacity-90 group-hover:opacity-100 truncate max-w-full text-center leading-none`}>
                          {av.sub.split(' ')[0]}
                        </span>
                              {isSelected && (
                                  <div className="absolute top-0.5 right-0.5 w-2.5 h-2.5 rounded-full bg-blue-600 border border-white flex items-center justify-center">
                                    <Check size={6} className="text-white font-bold" />
                                  </div>
                              )}
                            </button>
                        );
                      })}
                    </div>
                  </motion.div>
              )}

              {/* Edit Field Option Form */}
              {isEditing && (
                  <motion.form
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      onSubmit={handleSaveProfile}
                      className="mt-6 border-t border-slate-800/60 pt-4 text-left space-y-3"
                  >
                    <div className="space-y-1">
                      <label className="text-[8px] font-mono text-slate-500 font-bold uppercase tracking-wider block">{t('modifyNickname')}</label>
                      <input
                          type="text"
                          value={displayName}
                          onChange={(e) => setDisplayName(e.target.value)}
                          maxLength={30}
                          className="w-full bg-slate-950 border border-slate-800 focus:border-blue-500 rounded px-2.5 py-1.5 text-xs text-white placeholder-slate-600 outline-none transition-colors"
                          placeholder="E.g., GonzoCollector"
                      />
                    </div>
                    {updateError && (
                        <p className="text-[9px] text-red-400 font-mono leading-tight">{updateError}</p>
                    )}
                    <div className="flex gap-2 text-xs">
                      <button
                          type="button"
                          onClick={() => { setIsEditing(false); setDisplayName(user?.displayName || ''); setUpdateError(null); }}
                          className="flex-1 bg-slate-800 hover:bg-slate-705 text-slate-400 py-1 rounded text-[10px] font-bold font-mono uppercase cursor-pointer"
                      >
                        {t('cancel')}
                      </button>
                      <button
                          type="submit"
                          disabled={updating}
                          className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white py-1 rounded text-[10px] font-bold font-mono uppercase flex items-center justify-center gap-1.5 cursor-pointer"
                      >
                        {updating ? <RefreshCw size={10} className="animate-spin" /> : <Check size={10} />}
                        {updating ? t('saving') : t('save')}
                      </button>
                    </div>
                  </motion.form>
              )}

              {updateSuccess && (
                  <motion.div
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-4 py-1.5 px-3 bg-emerald-500/10 border border-emerald-500/20 rounded text-[9px] font-mono text-emerald-400 inline-flex items-center gap-1.5"
                  >
                    <Check size={11} /> {t('profileSuccess')}
                  </motion.div>
              )}

              {/* Pulsing Beacon "I Want to Connect" Toggle */}
              <div className="mt-6 pt-5 border-t border-slate-800/60 text-left space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-slate-300">{t('communityBeacon')}</span>
                  {connectOpen && (
                      <span className="flex items-center gap-1 text-[8px] font-mono font-extrabold uppercase tracking-widest text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20 shadow-[0_0_8px_rgba(16,185,129,0.2)]">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" /> {t('globalActive')}
                  </span>
                  )}
                </div>

                <button
                    type="button"
                    onClick={handleToggleConnect}
                    disabled={loadingConnect}
                    className={`w-full py-2.5 px-4 rounded-lg font-mono font-black text-xs uppercase cursor-pointer tracking-wider transition-all duration-300 border flex items-center justify-center gap-2.5 group hover:scale-102 active:scale-98 ${
                        connectOpen
                            ? 'bg-emerald-600/15 hover:bg-emerald-600/25 border-emerald-500/45 text-emerald-300 shadow-[0_0_15px_rgba(16,185,129,0.2)] animate-pulse'
                            : 'bg-indigo-600 hover:bg-indigo-505 border-indigo-550 text-white shadow-[0_4px_12px_rgba(99,102,241,0.2)] hover:shadow-[0_4px_20px_rgba(99,102,241,0.35)] animate-pulse'
                    }`}
                >
                  <Users size={14} className={connectOpen ? 'text-emerald-400' : 'text-indigo-150'} />
                  {connectOpen ? t('activeFriends') : t('wantConnect')}
                </button>

                <p className="text-[9px] font-mono text-slate-500 leading-normal">
                  {connectOpen
                      ? t('beaconActiveDesc')
                      : t('beaconInactiveDesc')}
                </p>
              </div>

              {/* Device Notifications Section */}
              <div className="mt-6 pt-5 border-t border-slate-800/60 text-left space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-slate-300">{t('deviceAlertStatus')}</span>
                  <span className={`text-[8px] font-mono font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded border uppercase leading-none ${
                      notificationPermission === 'granted'
                          ? 'text-emerald-400 bg-emerald-500/10 border-emerald-550/20 shadow-[0_0_8px_rgba(16,185,129,0.25)]'
                          : notificationPermission === 'denied'
                              ? 'text-rose-400 bg-rose-500/10 border-rose-500/20'
                              : 'text-amber-400 bg-amber-500/10 border-amber-500/20'
                  }`}>
                  {notificationPermission === 'granted' ? t('authorized') : notificationPermission === 'denied' ? t('blocked') : t('notSet')}
                </span>
                </div>

                {onRequestNotificationPermission && (
                    <button
                        type="button"
                        onClick={async () => {
                          await onRequestNotificationPermission();
                        }}
                        className={`w-full py-2.5 px-4 rounded-lg font-mono font-black text-xs uppercase cursor-pointer tracking-wider transition-all duration-300 border flex items-center justify-center gap-2.5 group hover:scale-102 active:scale-[0.98] ${
                            notificationPermission === 'granted'
                                ? 'bg-emerald-950/20 border-emerald-900 text-emerald-400 hover:bg-emerald-950/30'
                                : 'bg-[#1e293b] hover:bg-slate-800 border-slate-800 text-slate-300 hover:text-white'
                        }`}
                    >
                      <Bell size={14} className={notificationPermission === 'granted' ? 'text-emerald-400' : 'text-slate-400 group-hover:text-white'} />
                      {notificationPermission === 'granted' ? t('alertsOn') : t('enableAlerts')}
                    </button>
                )}

                <p className="text-[9px] font-mono text-slate-500 leading-normal">
                  {t('alertsDesc')}
                </p>

                {typeof window !== 'undefined' && window.self !== window.top && notificationPermission !== 'granted' && (
                    <div className="mt-2 p-2.5 bg-amber-950/25 border border-amber-500/20 rounded text-[9px] font-mono text-amber-400 relative overflow-hidden leading-normal flex items-start gap-2">
                      <span className="text-base leading-none">⚠️</span>
                      <div className="flex-1">
                        <strong>{t('sandboxNotice')}</strong> {t('sandboxDesc')} <a href={window.location.href} target="_blank" rel="noopener noreferrer" className="underline font-bold text-white hover:text-amber-300 cursor-pointer">{t('openNewTab')}</a> {t('toAuthorize')}
                      </div>
                    </div>
                )}
              </div>

              {/* GitHub Cloud Backup & Sync Segment */}
              <div className="mt-6 pt-5 border-t border-slate-800/60 text-left space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-slate-300">GitHub Cloud Sync</span>
                  <span className={`text-[8px] font-mono font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded border uppercase leading-none ${
                      githubToken
                          ? 'text-emerald-400 bg-emerald-500/10 border-emerald-555/20 shadow-[0_0_8px_rgba(16,185,129,0.25)]'
                          : 'text-amber-400 bg-amber-500/10 border-amber-500/20'
                  }`}>
                  {githubToken ? 'Connected' : 'Unlinked'}
                </span>
                </div>

                {!githubToken ? (
                    <div className="space-y-3">
                      <button
                          type="button"
                          onClick={handleConnectGithub}
                          disabled={githubStatus === 'checking'}
                          className="w-full py-2.5 px-4 rounded-lg bg-[#24292e] hover:bg-[#2c3137] border border-slate-750 text-white font-mono font-black text-xs uppercase cursor-pointer tracking-wider transition-all duration-300 flex items-center justify-center gap-2.5 shadow-lg shadow-black/30 hover:scale-[1.01] active:scale-[0.99] border-none"
                      >
                        <Github size={14} className="text-white" />
                        {githubStatus === 'checking' ? 'Connecting...' : 'Sync with GitHub'}
                      </button>

                      {githubStatus === 'error' && githubMessage && (
                          <div className="p-3 bg-red-950/20 border border-red-900/40 rounded text-[10px] font-mono text-red-400 leading-normal">
                            <p className="font-semibold mb-1">Configuration Needed</p>
                            <p className="text-[9px] text-slate-400 leading-relaxed">{githubMessage}</p>
                          </div>
                      )}

                      {showManualToken && (
                          <div className="p-3.5 bg-slate-900/60 border border-slate-800 rounded-lg space-y-2.5 text-left">
                            <span className="text-[9px] font-mono font-bold text-slate-400 uppercase block">Paste Token Link (Alternative)</span>
                            <p className="text-[8px] text-slate-500 font-mono leading-relaxed">
                              To bypass automatic OAuth registering, paste a safe Personal Access Token (classic or fine-grained token with <code className="text-slate-300 font-bold bg-slate-800 px-1 rounded">gist</code> access grants) below:
                            </p>
                            <div className="flex gap-2">
                              <input
                                  type="password"
                                  value={manualTokenInput}
                                  onChange={(e) => setManualTokenInput(e.target.value)}
                                  placeholder="ghp_xxxxxxxxxxxx"
                                  className="flex-1 bg-slate-950 border border-slate-850 rounded px-2.5 py-1 text-[11px] font-mono text-white placeholder-slate-600 outline-none focus:border-blue-500/50"
                              />
                              <button
                                  type="button"
                                  onClick={handleManualTokenSubmit}
                                  className="px-3 py-1 bg-indigo-600 hover:bg-indigo-505 text-white text-[9px] font-mono font-bold uppercase rounded cursor-pointer border-none"
                              >
                                Link
                              </button>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowManualToken(false)}
                                className="text-[8px] font-mono text-slate-500 hover:text-slate-400 underline block bg-transparent border-none p-0 cursor-pointer"
                            >
                              Hide alternative manual box
                            </button>
                          </div>
                      )}
                    </div>
                ) : (
                    <div className="space-y-3.5 bg-slate-950/40 border border-slate-800/80 rounded-xl p-3.5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {githubUser?.avatar_url ? (
                              <img
                                  src={githubUser.avatar_url}
                                  alt={githubUser.login}
                                  className="w-5 h-5 rounded-full border border-slate-700"
                                  referrerPolicy="no-referrer"
                              />
                          ) : (
                              <Github size={13} className="text-slate-400 animate-pulse" />
                          )}
                          <span className="text-[11px] font-mono font-bold text-slate-300">
                        @{githubUser?.login || 'Handshaking...'}
                      </span>
                        </div>
                        <button
                            type="button"
                            onClick={handleDisconnectGithub}
                            className="text-[9px] font-mono text-slate-500 hover:text-rose-400 uppercase tracking-wider transition-colors bg-transparent border-none p-0 cursor-pointer"
                        >
                          Disconnect
                        </button>
                      </div>

                      <p className="text-[8px] text-slate-500 font-mono leading-normal">
                        Securely preserve your album, sticker sets, achievements, packs count, and activity ledger inside a safe GitHub folder backup.
                      </p>

                      <div className="grid grid-cols-2 gap-2 pt-1">
                        <button
                            type="button"
                            onClick={handleGithubBackup}
                            disabled={isSyncingGithub}
                            className="py-1.5 px-3 rounded bg-blue-600/15 hover:bg-blue-600/25 border border-blue-500/40 text-blue-300 font-mono font-bold text-[10px] uppercase tracking-wider cursor-pointer transition-colors flex items-center justify-center gap-1.5"
                        >
                          <CloudLightning size={10} />
                          {isSyncingGithub ? 'Working...' : 'Backup'}
                        </button>
                        <button
                            type="button"
                            onClick={handleGithubRestore}
                            disabled={isSyncingGithub}
                            className="py-1.5 px-3 rounded bg-[#1e293b] hover:bg-slate-800 border border-slate-800 text-slate-300 font-mono font-bold text-[10px] uppercase tracking-wider cursor-pointer transition-colors flex items-center justify-center gap-1.5"
                        >
                          <RefreshCw size={10} className={isSyncingGithub ? 'animate-spin' : ''} />
                          Restore
                        </button>
                      </div>

                      {githubStatus === 'syncing' && (
                          <div className="flex items-center gap-2 text-[9px] font-mono text-blue-400 mt-2 animate-pulse">
                            <RefreshCw size={10} className="animate-spin" />
                            <span>{githubMessage}</span>
                          </div>
                      )}

                      {(githubStatus === 'success_backup' || githubStatus === 'success_restore') && (
                          <div className="p-2.5 bg-emerald-950/20 border border-emerald-900/40 rounded text-[9px] font-mono text-emerald-400 leading-normal flex items-start gap-1.5 mt-2">
                            <span>✅</span>
                            <span>{githubMessage}</span>
                          </div>
                      )}

                      {githubStatus === 'error' && (
                          <div className="p-2.5 bg-red-950/20 border border-red-900/40 rounded text-[9px] font-mono text-red-200 leading-normal flex items-start gap-1.5 mt-2">
                            <span>⚠️</span>
                            <span>{githubMessage}</span>
                          </div>
                      )}
                    </div>
                )}
              </div>

              <div className="h-px bg-slate-800/80 my-5" />

              {/* Detailed Auth Metadata Row */}
              <div className="space-y-3.5 text-left text-[10px] font-mono text-slate-400 leading-none">
                <div className="flex justify-between items-center text-slate-500">
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <Calendar size={11} /> {t('joinedDb')}
                  </div>
                  <span className="text-slate-300 font-bold">{joinedDate}</span>
                </div>
                <div className="flex justify-between items-center text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <CloudLightning size={11} /> {t('syncStatus')}
                  </div>
                  <span className="text-emerald-400 font-bold uppercase tracking-wide">{t('syncedCloud')}</span>
                </div>
                <div className="flex justify-between items-center text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <Database size={11} /> {t('cloudId')}
                  </div>
                  <span className="text-blue-400 font-bold select-all cursor-copy">{user?.uid ? `${user.uid.substring(0, 6)}...` : 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Superuser Controls */}
            {user?.email === 'gonzos978@gmail.com' && (
                <div className="bg-[#1e293b] border border-rose-500/30 rounded-xl p-5 shadow-xl relative overflow-hidden flex flex-col gap-3">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-rose-500 to-amber-500" />
                  <div className="flex items-center gap-1.5">
                    <Shield size={14} className="text-rose-400" />
                    <span className="text-[10px] font-mono font-bold tracking-wider text-rose-400 uppercase">{t('adminPanel')}</span>
                  </div>
                  <p className="text-[10px] text-slate-400 font-mono leading-relaxed text-left">
                    {t('adminDesc')}
                  </p>
                  <button
                      onClick={async () => {
                        if (onAddPacks) {
                          await onAddPacks(20);
                        }
                      }}
                      className="w-full flex items-center justify-center gap-1.5 py-2.5 bg-gradient-to-r from-rose-600 to-amber-600 hover:from-rose-500 hover:to-amber-500 text-white text-xs font-mono font-black uppercase rounded tracking-wider transition-all shadow-lg shadow-rose-600/20 active:scale-[0.98] cursor-pointer"
                  >
                    <Sparkles size={12} className="text-amber-200" /> {t('injectPacks')}
                  </button>
                  <div className="flex items-center justify-between text-[9px] font-mono text-slate-500">
                    <span>{t('leasedPacks')}</span>
                    <span className="text-slate-300 font-bold">{packs}</span>
                  </div>
                </div>
            )}

            {/* Quick Sign Out Option inside actions sidebar */}
            <div className="bg-[#1e293b] border border-slate-800 rounded-xl p-5 shadow-xl flex flex-col items-center gap-3">
              <button
                  onClick={onLogOut}
                  className="w-full flex items-center justify-center gap-2 py-2 border border-slate-800 hover:border-red-500/30 hover:bg-red-500/5 text-slate-400 hover:text-red-400 text-xs font-mono font-bold rounded transition-all uppercase cursor-pointer"
              >
                <LogOut size={13} /> {t('closeSession')}
              </button>
            </div>
          </div>

          {/* Right Side / Middle Body: Stats dashboard */}
          <div className="md:col-span-2 space-y-6">

            {/* Incoming Gifts Pending Mailbox */}
            <AnimatePresence>
              {receivedGifts.length > 0 && (
                  <motion.div
                      initial={{ opacity: 0, y: -15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -15 }}
                      className="bg-[#1e293b] border-2 border-amber-500/80 rounded-xl p-5 shadow-[0_0_20px_rgba(245,158,11,0.15)] relative overflow-hidden text-left"
                  >
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-yellow-400" />
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles size={14} className="text-amber-400 animate-bounce" />
                      <h4 className="font-mono text-xs font-black uppercase tracking-wider text-amber-500">
                        {t('giftingMailbox')} ({receivedGifts.length})
                      </h4>
                      <span className="text-[8px] font-mono bg-amber-500/10 text-amber-400 px-1.5 py-0.5 rounded border border-amber-500/20 uppercase font-black tracking-widest animate-pulse">
                    {t('newCopiesWaiting')}
                  </span>
                    </div>
                    <p className="text-[10px] text-slate-400 font-mono mb-4 leading-relaxed text-left">
                      {t('giftingMailboxDesc')}
                    </p>

                    <div className="space-y-2 max-h-[250px] overflow-y-auto pr-1">
                      {receivedGifts.map((gift) => {
                        const sticker = STICKER_MAP[gift.stickerId];
                        if (!sticker) return null;
                        return (
                            <div
                                key={gift.id}
                                className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-slate-950/50 rounded-lg border border-slate-800/80 hover:border-amber-500/30 transition-all gap-3 text-left"
                            >
                              <div className="flex items-center gap-3">
                                <Avatar photoURL={undefined} email={gift.senderEmail} size="sm" />
                                <div className="flex flex-col leading-tight">
                            <span className="text-xs text-white font-mono">
                              <span className="font-bold text-amber-400">{gift.senderName}</span> {t('sentSticker')}
                            </span>
                                  <span className="text-[10px] text-slate-300 font-bold font-sans mt-0.5">
                              {sticker.name} <span className="text-[9px] font-mono font-normal text-slate-500 uppercase">({sticker.rarity} #{sticker.num})</span>
                            </span>
                                </div>
                              </div>
                              <button
                                  onClick={() => handleClaimGift(gift)}
                                  disabled={claimingGiftId === gift.id}
                                  className="sm:w-auto w-full px-4 py-1.5 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-slate-950 font-mono font-black text-[10px] uppercase rounded tracking-wider shadow transition-all active:scale-[0.98] cursor-pointer flex items-center justify-center gap-1"
                              >
                                {claimingGiftId === gift.id ? t('claiming') : t('claimGift')}
                              </button>
                            </div>
                        );
                      })}
                    </div>
                  </motion.div>
              )}
            </AnimatePresence>

            {/* Main Completion Metric Dashboard */}
            <div className="bg-[#1e293b] border border-slate-800 rounded-xl p-6 shadow-xl relative overflow-hidden">
              {/* Ambient Background Grid Pattern */}
              <div className="absolute inset-0 opacity-5 pointer-events-none"
                   style={{ backgroundImage: 'radial-gradient(rgba(59, 130, 246, 0.4) 1px, transparent 1px)', backgroundSize: '12px 12px' }} />

              <p className="text-[10px] font-mono font-bold tracking-[0.3em] text-blue-500 uppercase mb-2">{t('albumCompletion')}</p>
              <h3 className="font-impact text-2xl tracking-wide text-white uppercase mb-6">{t('statsTitle')}</h3>

              {/* Major Numerics Trio */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-950/40 border border-slate-800/80 rounded-lg p-3.5 text-center flex flex-col justify-between">
                  <span className="text-[8px] font-mono text-slate-500 uppercase font-black leading-none mb-1">{t('uniqueCollected')}</span>
                  <div className="font-impact text-2xl text-white my-1">{ownedCount} <span className="text-slate-600 text-xs">/ {totalStickers}</span></div>
                  <span className="text-[9px] font-mono text-slate-400 leading-none">{t('activeAlbum')}</span>
                </div>
                <div className="bg-slate-950/40 border border-slate-800/80 rounded-lg p-3.5 text-center flex flex-col justify-between">
                  <span className="text-[8px] font-mono text-slate-500 uppercase font-black leading-none mb-1">{t('completionRate')}</span>
                  <div className="font-impact text-2xl text-blue-500 my-1">{uniquePct}%</div>
                  <div className="w-full bg-slate-800 h-1 rounded-full overflow-hidden mt-1">
                    <div className="h-full bg-blue-500" style={{ width: `${uniquePct}%` }} />
                  </div>
                </div>
                <button
                    type="button"
                    onClick={() => {
                      if (duplicates.length > 0) {
                        setShowDuplicates(!showDuplicates);
                      }
                    }}
                    disabled={duplicates.length === 0}
                    className={`bg-slate-950/40 border rounded-lg p-3.5 text-center flex flex-col justify-between items-center transition-all cursor-pointer select-none outline-none w-full active:scale-[0.98] group ${
                        duplicates.length === 0
                            ? 'opacity-50 border-slate-800/80 cursor-not-allowed'
                            : showDuplicates
                                ? 'border-emerald-500 bg-emerald-500/5 shadow-[0_0_12px_rgba(16,185,129,0.15)] animate-pulse'
                                : 'border-slate-800/80 hover:border-emerald-500/40 hover:bg-slate-900/30'
                    }`}
                >
                  <span className="text-[8px] font-mono text-slate-500 uppercase font-black leading-none mb-1 group-hover:text-emerald-400 transition-colors">{t('duplicateSpares')}</span>
                  <div className="font-impact text-2xl text-emerald-500 my-1">+{duplicates.length}</div>
                  <span className="text-[9px] font-mono text-slate-400 leading-none group-hover:text-slate-200 transition-colors">
                  {duplicates.length === 0
                      ? t('noSpares')
                      : showDuplicates
                          ? t('collapseSpares')
                          : t('moreDetails')
                  }
                </span>
                </button>
              </div>

              {/* Specific Rarity Projections ProgressBar list */}
              <div className="space-y-4">
                <div className="flex flex-col gap-1.5">
                  <div className="flex justify-between items-center text-[10px] font-mono">
                    <div className="flex items-center gap-1.5 font-bold text-slate-300">
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-400" /> {t('commonStickers')}
                    </div>
                    <span className="font-bold text-slate-400">{ownedByRarity.common} / {stickersByRarity.common.length} ({rarityPcts.common}%)</span>
                  </div>
                  <div className="w-full bg-slate-950/60 h-2 rounded border border-slate-800/80 overflow-hidden">
                    <div className="h-full bg-slate-400 transition-all duration-700" style={{ width: `${rarityPcts.common}%` }} />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex justify-between items-center text-[10px] font-mono">
                    <div className="flex items-center gap-1.5 font-bold text-emerald-400">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_#10b981]" /> {t('rareStickers')}
                    </div>
                    <span className="font-bold text-emerald-400">{ownedByRarity.rare} / {stickersByRarity.rare.length} ({rarityPcts.rare}%)</span>
                  </div>
                  <div className="w-full bg-slate-950/60 h-2 rounded border border-slate-800/80 overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all duration-700" style={{ width: `${rarityPcts.rare}%` }} />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex justify-between items-center text-[10px] font-mono">
                    <div className="flex items-center gap-1.5 font-bold text-blue-400">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 shadow-[0_0_6px_#3b82f6]" /> {t('legendaryStickers')}
                    </div>
                    <span className="font-bold text-blue-400">{ownedByRarity.legendary} / {stickersByRarity.legendary.length} ({rarityPcts.legendary}%)</span>
                  </div>
                  <div className="w-full bg-slate-950/60 h-2 rounded border border-slate-800/80 overflow-hidden">
                    <div className="h-full bg-blue-500 transition-all duration-700" style={{ width: `${rarityPcts.legendary}%` }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Reserved Spares Expansion Panel */}
            <AnimatePresence>
              {showDuplicates && sortedDuplicateStickers.length > 0 && (
                  <motion.div
                      initial={{ opacity: 0, height: 0, y: -10 }}
                      animate={{ opacity: 1, height: 'auto', y: 0 }}
                      exit={{ opacity: 0, height: 0, y: -10 }}
                      className="bg-[#1e293b] border border-emerald-500/30 rounded-xl p-6 shadow-xl relative overflow-hidden"
                  >
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 to-teal-500" />
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Sparkles size={14} className="text-emerald-400 animate-pulse" />
                        <h4 className="font-mono text-xs font-bold uppercase tracking-wider text-slate-200">
                          {t('myDuplicates')} ({duplicates.length})
                        </h4>
                      </div>
                      <button
                          onClick={() => setShowDuplicates(false)}
                          className="text-[10px] font-mono text-slate-400 hover:text-white uppercase font-bold cursor-pointer"
                      >
                        {t('close')}
                      </button>
                    </div>
                    <p className="text-[10px] text-slate-400 font-mono mb-4 leading-relaxed text-left">
                      {t('clickDuplicateDesc')}
                    </p>

                    <div className="flex flex-wrap gap-4 max-h-[360px] overflow-y-auto pr-1">
                      {sortedDuplicateStickers.map((sticker) => {
                        const count = duplicates.filter(id => id === sticker.id).length;
                        return (
                            <StickerCard
                                key={sticker.id}
                                sticker={sticker}
                                small
                                duplicateCount={count}
                                onClick={() => setSelectedInspectSticker(sticker)}
                            />
                        );
                      })}
                    </div>
                  </motion.div>
              )}
            </AnimatePresence>

            {/* holographic inspector modal */}
            <AnimatePresence>
              {selectedInspectSticker && (
                  <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md"
                      onClick={() => setSelectedInspectSticker(null)}
                  >
                    <motion.div
                        initial={{ scale: 0.9, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.9, y: 20 }}
                        className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                      {/* Close button */}
                      <button
                          onClick={() => setSelectedInspectSticker(null)}
                          className="absolute top-4 right-4 text-slate-400 hover:text-white bg-slate-800/60 hover:bg-slate-800/90 rounded-full p-2 transition-colors z-20 cursor-pointer border border-slate-700/50"
                      >
                        <X size={16} />
                      </button>

                      <div className="p-6 sm:p-8 flex flex-col sm:flex-row gap-8 items-center">
                        {/* Visual Card Representation */}
                        <div className="flex-shrink-0">
                          <div
                              className={`w-44 h-60 rounded-xl flex flex-col items-center justify-between p-4 relative overflow-hidden select-none border-2 shadow-2xl ${
                                  selectedInspectSticker.rarity === 'legendary'
                                      ? 'border-blue-500/80 shadow-[0_0_25px_rgba(59,130,246,0.5)]'
                                      : selectedInspectSticker.rarity === 'rare'
                                          ? 'border-emerald-500/80 shadow-[0_0_25px_rgba(16,185,129,0.4)]'
                                          : 'border-slate-600/50'
                              }`}
                              style={
                                selectedInspectSticker.teamId && TEAMS.find(t => t.id === selectedInspectSticker.teamId)
                                    ? {
                                      backgroundImage: `linear-gradient(150deg, ${TEAMS.find(t => t.id === selectedInspectSticker.teamId)!.c1}ee, ${TEAMS.find(t => t.id === selectedInspectSticker.teamId)!.c1}ad, ${TEAMS.find(t => t.id === selectedInspectSticker.teamId)!.c2}33)`
                                    }
                                    : selectedInspectSticker.rarity === 'legendary'
                                        ? { backgroundImage: 'linear-gradient(150deg, #1e1b4b, #312e81, #1e1b4b)' }
                                        : selectedInspectSticker.rarity === 'rare'
                                            ? { backgroundImage: 'linear-gradient(150deg, #111827, #1f2937, #111827)' }
                                            : { backgroundColor: '#1e293b' }
                              }
                          >
                            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10 pointer-events-none" />

                            <span className="absolute top-1.5 left-2 text-[8px] text-white/60 font-mono font-bold tracking-tighter">
                          CODE_{String(selectedInspectSticker.num).padStart(3, '0')}
                        </span>

                            <div className="absolute top-1.5 right-2 flex gap-1">
                              {selectedInspectSticker.rarity === 'legendary' && <div className="w-2 h-2 rounded-full bg-blue-400 shadow-[0_0_6px_#3b82f6]" />}
                              {selectedInspectSticker.rarity === 'rare' && <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_6px_#10b981]" />}
                            </div>

                            {selectedInspectSticker.type === 'special' && (
                                <span className="text-5xl filter drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)]">{selectedInspectSticker.icon}</span>
                            )}

                            {selectedInspectSticker.type === 'badge' && selectedInspectSticker.teamId && TEAMS.find(t => t.id === selectedInspectSticker.teamId)?.flag && (
                                <img
                                    src={`https://flagcdn.com/w160/${TEAMS.find(t => t.id === selectedInspectSticker.teamId)!.flag}.png`}
                                    alt=""
                                    className="w-24 h-18 object-cover rounded shadow-lg border border-white/10 mt-2"
                                    referrerPolicy="no-referrer"
                                    crossOrigin="anonymous"
                                />
                            )}

                            {selectedInspectSticker.type === 'player' && (
                                <span className="text-5xl filter drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)]">
                            {selectedInspectSticker.pos ? POS_ICON[selectedInspectSticker.pos] : '⚽'}
                          </span>
                            )}

                            {selectedInspectSticker.type === 'player' && selectedInspectSticker.teamId && TEAMS.find(t => t.id === selectedInspectSticker.teamId)?.flag && (
                                <img
                                    src={`https://flagcdn.com/w40/${TEAMS.find(t => t.id === selectedInspectSticker.teamId)!.flag}.png`}
                                    alt=""
                                    className="absolute top-6 right-2 w-5 h-4 object-cover rounded-[1px] opacity-90 border border-white/5"
                                    referrerPolicy="no-referrer"
                                    crossOrigin="anonymous"
                                />
                            )}

                            {selectedInspectSticker.type === 'player' && selectedInspectSticker.pos && (
                                <span className="rounded px-2 py-0.5 text-[9px] text-white font-mono font-bold tracking-widest uppercase bg-slate-950/80 backdrop-blur-sm border border-slate-800">
                            {selectedInspectSticker.pos}
                          </span>
                            )}

                            {duplicates.filter(d => d === selectedInspectSticker.id).length > 0 && (
                                <div className="absolute bottom-11 right-2 bg-amber-500 text-slate-950 font-mono font-black text-[9px] px-1.5 py-0.5 rounded border border-amber-400 shadow-lg z-10">
                                  x{duplicates.filter(d => d === selectedInspectSticker.id).length + 1}
                                </div>
                            )}

                            <div className="w-full bg-slate-950/40 backdrop-blur-[2px] py-2 px-1.5 flex items-center justify-center border border-white/5 rounded gap-1.5 shadow-inner">
                              {selectedInspectSticker.teamId && TEAMS.find(t => t.id === selectedInspectSticker.teamId)?.flag && (
                                  <img
                                      src={`https://flagcdn.com/w20/${TEAMS.find(t => t.id === selectedInspectSticker.teamId)!.flag}.png`}
                                      alt=""
                                      className="w-3.5 h-2.5 object-cover rounded-[1px] shadow-sm"
                                      referrerPolicy="no-referrer"
                                      crossOrigin="anonymous"
                                  />
                              )}
                              <span className="text-[10px] text-white font-black font-sans uppercase tracking-[0.05em] truncate">
                            {selectedInspectSticker.name}
                          </span>
                            </div>
                          </div>
                        </div>

                        {/* Inspect Meta Column */}
                        <div className="flex-1 space-y-4 text-left w-full">
                          <div>
                            <h5 className="text-[10px] font-mono text-slate-500 uppercase tracking-widest leading-none mb-1">
                              {t('duplicateDetails')}
                            </h5>
                            <h4 className="font-sans text-xl font-bold text-white tracking-tight">
                              {selectedInspectSticker.name}
                            </h4>
                          </div>

                          <div className="space-y-2 text-xs font-mono text-slate-400">
                            <div className="flex justify-between py-1 border-b border-slate-800/60">
                              <span>{t('cardNumber')}</span>
                              <span className="text-white font-bold">#{selectedInspectSticker.num}</span>
                            </div>
                            {selectedInspectSticker.pos && (
                                <div className="flex justify-between py-1 border-b border-slate-800/60">
                                  <span>{t('positionAssigned')}</span>
                                  <span className="text-white font-bold">{selectedInspectSticker.pos}</span>
                                </div>
                            )}
                            {selectedInspectSticker.teamId && TEAMS.find(t => t.id === selectedInspectSticker.teamId) && (
                                <div className="flex justify-between py-1 border-b border-slate-800/60 font-sans">
                                  <span>{t('countryRoster')}</span>
                                  <span className="text-blue-400 font-bold">
                              {TEAMS.find(t => t.id === selectedInspectSticker.teamId)!.name}
                            </span>
                                </div>
                            )}
                            <div className="flex justify-between py-1 border-b border-slate-800/60">
                              <span>{t('stickerClass')}</span>
                              <span className="text-indigo-400 uppercase font-black">{selectedInspectSticker.type}</span>
                            </div>
                            <div className="flex justify-between py-1">
                              <span>{t('specTier')}</span>
                              <span className={`capitalize font-bold ${
                                  selectedInspectSticker.rarity === 'legendary'
                                      ? 'text-blue-400'
                                      : selectedInspectSticker.rarity === 'rare'
                                          ? 'text-emerald-400'
                                          : 'text-slate-400'
                              }`}>
                            {selectedInspectSticker.rarity}
                          </span>
                            </div>
                          </div>

                          <div className="bg-[#1e293b] p-3 rounded-lg border border-slate-800 flex items-start gap-2 text-slate-400 font-sans">
                            <Info size={14} className="text-blue-400 mt-0.5 flex-shrink-0" />
                            <p className="text-[10px] leading-normal">
                              {t('spareCardNotice')}
                            </p>
                          </div>

                          {/* Gifting Zone */}
                          <div className="border-t border-slate-800/80 pt-4 mt-2 space-y-3">
                            <h6 className="text-[9px] font-mono font-bold tracking-wider text-amber-500 uppercase flex items-center gap-1.5 leading-none">
                              🎁 {t('sendAsGift')}
                            </h6>
                            {friends.length === 0 ? (
                                <p className="text-[10px] text-slate-500 font-sans bg-slate-950/20 p-2.5 rounded border border-slate-900 leading-normal">
                                  {t('noFriendsGiftNotice')}
                                </p>
                            ) : (
                                <div className="space-y-1.5 max-h-[140px] overflow-y-auto pr-1">
                                  {friends.map((friend) => (
                                      <div
                                          key={friend.userId}
                                          className="flex items-center justify-between p-2 bg-slate-950/40 rounded border border-slate-850 hover:bg-slate-900/30 transition-all gap-2"
                                      >
                                        <div className="flex items-center gap-2 min-w-0">
                                          <Avatar photoURL={friend.photoURL} email={friend.email} size="sm" />
                                          <div className="flex flex-col min-w-0 leading-none">
                                    <span className="text-[11px] text-slate-200 font-bold truncate mb-0.5">
                                      {friend.displayName || friend.email.split('@')[0]}
                                    </span>
                                            <span className="text-[8px] font-mono text-slate-500 truncate">
                                      {friend.email}
                                    </span>
                                          </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleSendGift(friend)}
                                            disabled={giftingInProgress === friend.userId}
                                            className="px-2.5 py-1.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-mono font-black text-[9px] uppercase rounded transition-all flex items-center gap-1 cursor-pointer select-none active:scale-95 disabled:scale-100 disabled:opacity-50"
                                        >
                                          {giftingInProgress === friend.userId ? t('sending') : t('giftCopy')}
                                        </button>
                                      </div>
                                  ))}
                                </div>
                            )}
                            {giftSuccessMessage && (
                                <div className="p-2 border border-emerald-500/20 bg-emerald-500/5 text-emerald-400 font-mono text-[9px] rounded text-center animate-pulse">
                                  {giftSuccessMessage}
                                </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
              )}
            </AnimatePresence>

            {/* Achievements Matrix Board */}
            <div className="bg-[#1e293b] border border-slate-800 rounded-xl p-6 shadow-xl relative overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <div>
                  <p className="text-[10px] font-mono font-bold tracking-[0.3em] text-amber-500 uppercase mb-1">{t('collectorMilestones')}</p>
                  <h4 className="font-impact text-xl tracking-wide text-white uppercase">{t('achievementsMap')}</h4>
                </div>
                <div className="flex items-center gap-2 bg-slate-950/60 border border-slate-800/85 px-3 py-1.5 rounded-lg">
                <span className="text-sm font-bold text-white font-mono flex items-center gap-1">
                  🔥 {streak} <span className="text-[10px] font-mono text-slate-500 uppercase font-bold">{t('dayStreak')}</span>
                </span>
                  <span className="text-slate-700 font-mono">|</span>
                  <span className="text-xs font-bold text-amber-400 font-mono">
                  {achievements.length} / {ACHIEVEMENTS.length} {t('cleared')}
                </span>
                </div>
              </div>

              <p className="text-[10px] font-mono text-slate-400 leading-normal mb-5 text-left">
                {t('milestonesDesc')}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {ACHIEVEMENTS.map((ach) => {
                  const isUnlocked = achievements.includes(ach.id);
                  return (
                      <div
                          key={ach.id}
                          className={`flex gap-3.5 p-3.5 rounded-xl border relative overflow-hidden transition-all duration-300 text-left ${
                              isUnlocked
                                  ? 'bg-gradient-to-br from-slate-900 to-[#1e293b]/70 border-amber-500/30'
                                  : 'bg-slate-950/20 border-slate-800/60 opacity-60'
                          }`}
                      >
                        {isUnlocked && (
                            <div className="absolute top-0 right-0 py-0.5 px-2 bg-amber-500/10 border-l border-b border-amber-500/20 text-[7px] font-mono font-black text-amber-400 uppercase tracking-widest rounded-bl">
                              {t('unlocked')}
                            </div>
                        )}
                        <div className="text-2xl flex-shrink-0 flex items-center justify-center bg-slate-950/40 w-11 h-11 border border-slate-800/80 rounded-lg">
                          {isUnlocked ? ach.icon : "🔒"}
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col justify-between leading-snug">
                          <div>
                            <h5 className={`text-xs font-bold font-sans truncate ${isUnlocked ? 'text-white' : 'text-slate-500'}`}>
                              {ach.title}
                            </h5>
                            <p className="text-[10px] text-slate-400 font-sans mt-0.5 leading-normal">
                              {ach.description}
                            </p>
                          </div>
                          <div className="mt-2 flex items-center justify-between">
                        <span className="text-[8px] font-mono text-slate-500 uppercase tracking-tight truncate max-w-[140px]" title={ach.conditionDescription}>
                          {t('goal')} {ach.conditionDescription}
                        </span>
                            <span className={`text-[8px] font-mono px-1.5 py-0.5 rounded uppercase font-black tracking-wider border leading-none ${
                                isUnlocked
                                    ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
                                    : 'text-slate-500 bg-slate-800/30 border-slate-800/50'
                            }`}>
                          +{ach.rewardPacks} {ach.rewardPacks > 1 ? t('packs') : t('packs')}
                        </span>
                          </div>
                        </div>
                      </div>
                  );
                })}
              </div>
            </div>

            {/* List of Legendary/Highlight Stickers Collected */}
            <div className="bg-[#1e293b] border border-slate-800 rounded-xl p-6 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Trophy size={16} className="text-amber-400" />
                  <h4 className="font-mono text-xs font-bold uppercase tracking-wider text-slate-200">{t('legendaryHighlights')}</h4>
                </div>
                <span className="text-[9px] font-mono text-slate-500 leading-none">{t('showingTop5')}</span>
              </div>

              {legendaryStickersCollected.length === 0 ? (
                  <div className="py-8 bg-slate-950/30 border border-slate-800 border-dashed rounded-lg flex flex-col items-center justify-center text-center p-4">
                    <Sparkles size={24} className="text-slate-600 mb-2" />
                    <p className="text-[10px] font-mono text-slate-500 uppercase leading-relaxed font-bold">{t('noLegendaries')}</p>
                    <p className="text-[9px] font-mono text-slate-600 mt-1 max-w-[280px]">{t('noLegendariesDesc')}</p>
                  </div>
              ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                    {legendaryStickersCollected.map((sticker) => (
                        <div
                            key={sticker.id}
                            className="p-2.5 bg-slate-950/50 border border-blue-500/20 rounded shadow-md flex flex-col items-center justify-between text-center min-h-[90px] relative overflow-hidden"
                        >
                          <div className="absolute top-1 left-1.5 text-[8px] font-mono text-slate-600">
                            #{sticker.num}
                          </div>
                          <div className="text-xl filter drop-shadow-[0_2px_4px_rgba(59,130,246,0.3)] my-2">
                            {sticker.type === 'special' ? (sticker.icon || '🏆') : '⚡'}
                          </div>
                          <div className="text-[8px] font-mono text-white/90 truncate w-full font-bold uppercase leading-tight select-none">
                            {sticker.name}
                          </div>
                        </div>
                    ))}
                  </div>
              )}
            </div>

            {/* Wipe Progress / Safe Lock Form */}
            <div className="bg-[#1e293b]/70 border border-slate-800/80 rounded-xl p-6 shadow-xl space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-red-500/10 border border-red-500/20 text-red-400 rounded">
                  <Trash2 size={16} />
                </div>
                <div className="space-y-1">
                  <h4 className="font-mono text-xs font-bold text-red-400 uppercase tracking-wider">{t('wipeTitle')}</h4>
                  <p className="text-[10px] text-slate-400 leading-relaxed max-w-lg">
                    {t('wipeDesc')}
                  </p>
                </div>
              </div>

              {!showResetConfirm ? (
                  <div className="flex justify-end pt-2">
                    <button
                        onClick={() => setShowResetConfirm(true)}
                        className="px-4 py-2 bg-red-500/10 border border-red-500/20 font-mono text-[10px] text-red-400 rounded uppercase font-bold hover:bg-red-500 hover:text-white transition-all cursor-pointer"
                    >
                      {t('initiateWipe')}
                    </button>
                  </div>
              ) : (
                  <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-slate-950 p-4 border border-red-500/20 rounded-lg space-y-3"
                  >
                    <div className="flex items-center gap-1.5 text-[10px] font-mono text-red-400 font-bold">
                      <Info size={12} /> {t('doubleCheckGuard')}
                    </div>
                    <div className="space-y-1">
                      <p className="text-[9px] text-slate-400 font-mono leading-relaxed">
                        {t('keyInReset')}
                      </p>
                      <input
                          type="text"
                          value={resetInput}
                          onChange={(e) => setResetInput(e.target.value)}
                          className="w-full bg-slate-900 border border-slate-800 focus:border-red-500 rounded px-2.5 py-1.5 text-xs text-red-400 placeholder-slate-700 font-mono outline-none"
                          placeholder={t('executeWipe')}
                      />
                    </div>
                    <div className="flex justify-end gap-2 text-[10px] font-mono">
                      <button
                          type="button"
                          onClick={() => { setShowResetConfirm(false); setResetInput(''); }}
                          className="px-3 py-1.5 bg-slate-900 hover:bg-slate-850 text-slate-400 rounded font-bold uppercase cursor-pointer"
                      >
                        {t('abortSequence')}
                      </button>
                      <button
                          type="button"
                          onClick={executeReset}
                          disabled={resetInput !== 'RESET' || resetting}
                          className="px-4 py-1.5 bg-red-600 disabled:opacity-30 hover:bg-red-500 text-white rounded font-bold uppercase flex items-center gap-1.5 cursor-pointer"
                      >
                        {resetting ? <RefreshCw size={11} className="animate-spin" /> : <Trash2 size={11} />}
                        {t('executeWipe')}
                      </button>
                    </div>
                  </motion.div>
              )}
            </div>

          </div>

        </div>
      </div>
  );
};
